<script lang="ts" setup>
import { mdiWifiOff } from "@mdi/js";
import { captureException as sentryCaptureException, setUser as setSentryUser } from "@sentry/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useToggle } from "@vueuse/core";
import dayjs from "dayjs";
import { storeToRefs } from "pinia";
import { isTokenExpired } from "pocketbase";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";

import { pb } from "#api/pocketbase";
import LayoutStack from "#components/layout/LayoutStack.vue";
import TheAppBar from "#components/single/TheAppBar.vue";
import TheAppConfirmDialog from "#components/single/TheAppConfirmDialog.vue";
import TheAppDebug from "#components/single/TheAppDebug.vue";
import TheAppDrawer from "#components/single/TheAppDrawer.vue";
import TheAppSnackbar from "#components/single/TheAppSnackbar.vue";
import { useAuthAccount } from "#composables/use-auth-account";
import { useSnackbar } from "#composables/use-snackbar";
import { STORAGE_KEY_PREFIX } from "#composables/use-storage-state";
import { appConfig } from "#config/app";
import { mapRecordToAccount } from "#modules/account/account.utils";
import { useApiHealthcheck, useFeatureFlagsQuery } from "#modules/app/app.queries";
import { useAppStore } from "#modules/app/app.store";
import { mapRecordToCollaborator } from "#modules/collaborator/collaborator.utils";
import { mapRecordToComposer } from "#modules/composer/composer.utils";
import { contentKeys, getContentByCode } from "#modules/content/content.queries";
import { useCurrentSeasonsQuery } from "#modules/season/season.queries";
import { useSeasonStore } from "#modules/season/season.store";
import {
  isAfterSeasonSubmissionsStart,
  isBeforeSeasonSubmissionsEnd,
} from "#modules/season/season.utils";
import { FORMAT_DATE_READABLE, formatDate } from "#utilities/date";
import { getError, isPocketbaseError } from "#utilities/error";
import { enDash } from "#utilities/typography";

const route = useRoute();
const queryClient = useQueryClient();
const accountStore = reactive(useAuthAccount());

const [showMobileDrawer, toggleMobileDrawer] = useToggle(false);

const loadingStatus = ref<"loading" | "error" | "ready">("loading");

const { error: apiHealthError, isLoading: apiHealthLoading } = useApiHealthcheck();

const loading = computed(() => {
  return (
    loadingStatus.value === "loading" ||
    apiHealthLoading.value ||
    featureFlagsLoading.value ||
    // Seasons should be loaded before app mounts, since many views/queries depend on the selected season
    seasonsLoading.value
  );
});

const appStore = useAppStore();
const { notifyWarning } = useSnackbar();
const seasonStore = useSeasonStore();

const CURRENT_SEASON__KEY = `${STORAGE_KEY_PREFIX}-current-season`;

const { data: seasonData, isLoading: seasonsLoading } = useCurrentSeasonsQuery();
watch(seasonData, (newSeasonData) => {
  seasonStore.activeSeasons = newSeasonData ?? [];

  // Hydrate previously selected current season (invalid values will be automatically overwritten)
  const storedCurrentSeasonId = localStorage.getItem(CURRENT_SEASON__KEY);
  const previousCurrentSeason = newSeasonData?.find((s) => s.id === storedCurrentSeasonId);
  seasonStore.currentSeason = previousCurrentSeason ?? newSeasonData?.[0];
});

// Persist previously selected current season (for future hydration)
// NOTE: Non-active seasons will be ignored as they will be overwritten upon page refresh since they
//         will not be present in list of current seasons.
const { currentSeason } = storeToRefs(seasonStore);
watch(currentSeason, (newCurrentSeason) => {
  if (newCurrentSeason?.current) {
    localStorage.setItem(CURRENT_SEASON__KEY, newCurrentSeason.id);
  }
});

const { data: featureFlagsData, isLoading: featureFlagsLoading } = useFeatureFlagsQuery();
watch(featureFlagsData, (newFeatureFlags) => {
  appStore.featureFlagList = newFeatureFlags ?? [];
});

const { mobile } = useDisplay();

const seasonDisplay = computed(() => {
  if (!seasonStore.currentSeason) return "N/A";

  const { submissionsEnd, submissionsStart } = seasonStore.currentSeason;

  return `${formatDate(submissionsStart, FORMAT_DATE_READABLE)} ${enDash} ${formatDate(submissionsEnd, FORMAT_DATE_READABLE)}`;
});

const submissionProgress = computed(() => {
  const currentSeason = seasonStore.currentSeason;
  if (!currentSeason) return null;

  const { currentSubmissions, totalSubmissionLimit } = currentSeason;
  if (!totalSubmissionLimit || currentSubmissions === undefined) return null;

  return {
    current: currentSubmissions,
    limit: totalSubmissionLimit,
    percent: Math.min(100, (currentSubmissions / totalSubmissionLimit) * 100),
    visible:
      isAfterSeasonSubmissionsStart(currentSeason) &&
      isBeforeSeasonSubmissionsEnd(currentSeason, {
        type: "after",
        value: dayjs.duration(1, "week"),
      }),
  };
});

onMounted(async () => {
  // Dev-specific workaround to race condition caused by conditional/async import of MSW.
  //   See 'main.ts' for more details/explanation.
  // NOTE: MSW must be ready before sending any further API requests (or they will fail)!
  if (appConfig.development && window.mswReady) {
    await window.mswReady;
  }

  // TODO: Load additional data

  // No need to wait for authentication checks, as any "blocking" checks are performed synchronously.
  //   While the auth refresh call is async, it uses the same auth token (no separate refresh token)
  //   and therefore does not need to block subsequent work.
  checkAuthentication();

  // Only prefetch application terms when not authenticated
  if (!pb.authStore.isValid) {
    queryClient.prefetchQuery({
      queryKey: contentKeys.byCode(ref("applicationTerms")),
      queryFn: () => getContentByCode("applicationTerms"),
    });
  }

  loadingStatus.value = "ready";
});

const checkAuthentication = () => {
  // Ensure auth store is cleaned up if stored auth state is invalid
  if (!pb.authStore.isValid || !pb.authStore.record) {
    pb.authStore.clear();
    return;
  }

  // Only refresh token if expiring shortly (less than expiry) to avoid unnecessary auth store updates.
  // For a while opening multiple tabs simultaneously seemed to cause automatic logout (not in dev),
  //   which appeared to be caused to clearing the auth store when refreshing auth token fails.
  //   Changing this error handling to only logout for specific auth-related error codes, along with
  //   no longer always refreshing auth on page load, seems to have mitigated this issue.
  const tokenRefreshExpiryThreshold = 60 * 60 * 24 * 5; // 5 days
  if (pb.authStore.isValid && isTokenExpired(pb.authStore.token, tokenRefreshExpiryThreshold)) {
    pb.collection(pb.authStore.record.collectionName)
      .authRefresh()
      .catch((e) => {
        // Only trigger logout for auth-related errors (see note above)
        const authErrorCodes = [401, 403, 404];
        if (isPocketbaseError(e) && authErrorCodes.includes(e.status)) {
          pb.authStore.clear();
          return;
        }

        sentryCaptureException(e, {
          extra: { originalError: e.originalError, statusCode: e.status },
        });
        notifyWarning("Unable to refresh authentication; login may be required.");
      });
  }
};

/**
 * Previous auth store state (for comparisons with current state)
 *
 * By comparing previous state with current auth store update value, actions can be taken to reflect
 *   the performed action. This can work with both "immediate" or "lazy" auth store change handler,
 *   since the auth store is synchronously set upon app load (vs having to send request to API first).
 */
const prevModel = ref(pb.authStore.record);
pb.authStore.onChange(async (_token, model) => {
  const wasAuthenticated = !!prevModel.value;
  const nowAuthenticated = !!model;
  prevModel.value = model;

  if (appConfig.development) {
    // eslint-disable-next-line no-console
    console.debug("AuthStore.onChange", model, wasAuthenticated, nowAuthenticated);
  }

  // When user becomes authenticated (ie. via login), a reload should be triggered to ensure proper
  //   page state. The authenticated user will then be automatically detected/set upon load.
  if (!wasAuthenticated && nowAuthenticated) {
    const { redirectUrl } = route.query;
    const finalUrl = redirectUrl && typeof redirectUrl === "string" ? redirectUrl : "/dashboard";
    window.location.replace(finalUrl);
    return;
  }

  // When user was already authenticated and auth is updated, ensure authenticated user is updated.
  //   This will usually occur through token refresh, but may also occur on initial page load.
  if (wasAuthenticated && nowAuthenticated) {
    const accountBase = mapRecordToAccount(model);
    const account =
      accountBase.type === "composer" ? mapRecordToComposer(model) : mapRecordToCollaborator(model);
    accountStore.setAccount(account);
    setSentryUser({ email: account.email, id: account.id, type: account.type });
    return;
  }

  // When user becomes unauthenticated (ie. via logout), a reload should be triggered to ensure proper
  //   page state. The authenticated user should not be removed from cache (to avoid flickers), as it
  //   will no longer be present upon load.
  if (wasAuthenticated && !nowAuthenticated) {
    window.location.replace("/auth/login");
    return;
  }

  // When user was already unauthenticated and auth is cleared again, ensure auth store is cleared.
  if (!wasAuthenticated && !nowAuthenticated) {
    accountStore.clearAccount();
    setSentryUser(null);
    return;
  }
}, true);
</script>

<template>
  <VApp>
    <TheAppBar @toggle-drawer="toggleMobileDrawer" />
    <VSystemBar
      v-if="seasonStore.currentSeason"
      class="season-bar"
      :color="seasonStore.withinSubmissionDates ? 'primary-lighten-2' : 'warning-lighten-2'"
      :height="submissionProgress?.visible ? 48 : 24"
    >
      <LayoutStack align-items="center" direction="row" :spacing="0">
        <strong>{{ seasonStore.currentSeason.name }}</strong>
        <VBadge
          v-if="seasonStore.activeSeasons.length > 1"
          v-tooltip="`${seasonStore.activeSeasons.length - 1} other active season(s)`"
          color="warning"
          dot
          inline
        />
        <strong>&ensp;//&ensp;</strong>
        <div>{{ !mobile ? `Submissions Open: ${seasonDisplay}` : seasonDisplay }}</div>
      </LayoutStack>

      <LayoutStack v-if="submissionProgress?.visible" align-items="center" direction="row">
        <VProgressLinear
          bg-opacity="0.3"
          color="surface"
          height="6"
          :model-value="submissionProgress.percent"
          rounded
          :style="{ width: mobile ? '80px' : '120px' }"
          width="120px"
        />
        <span class="text-caption flex-shrink-0">
          {{ submissionProgress.current }} / {{ submissionProgress.limit }} total submissions
        </span>
      </LayoutStack>
    </VSystemBar>

    <template v-if="loading">
      <VProgressCircular class="ma-auto" indeterminate size="80" width="6" />
    </template>
    <template v-else-if="apiHealthError">
      <VMain>
        <LayoutStack align-items="center" class="w-100 h-100" justify-content="center">
          <VCard class="offline-card pa-6 w-full" color="error" variant="tonal">
            <VIcon class="d-block mx-auto" :icon="mdiWifiOff" size="48" />
            <VCardTitle>Unknown Error</VCardTitle>
            <VCardText>
              Oh no, it looks like there's an unexpected issue! Check back soon!
              <VAlert v-if="apiHealthError" class="mt-4" type="error">
                {{ getError(apiHealthError) }}
              </VAlert>
            </VCardText>
          </VCard>
        </LayoutStack>
      </VMain>
    </template>
    <template v-else>
      <!-- Drawer is only shown for collaborators currently -->
      <TheAppDrawer
        v-if="accountStore.authenticated && accountStore.isCollaborator"
        :show-on-mobile="showMobileDrawer"
        @toggle-drawer="toggleMobileDrawer"
      />

      <VMain>
        <RouterView />
      </VMain>
    </template>

    <TheAppConfirmDialog />
    <TheAppDebug />
    <TheAppSnackbar />

    <VueQueryDevtools v-if="!appConfig.production" />
  </VApp>
</template>

<style lang="scss" scoped>
.season-bar {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.offline-card {
  margin: auto;
  max-width: 500px;
}
</style>
